import React from 'react';
import { usePortalContext } from '../context/PortalContext';

interface StaffAppRole {
  id: string;
  value: string;
  displayName: string;
  description: string;
}

interface StaffRoleAssignment {
  assignmentId: string;
  principalId: string;
  principalDisplayName: string;
  principalType: string;
  appRoleId: string;
  appRoleValue: string;
  appRoleDisplayName: string;
  createdDateTime?: string;
}

interface StaffRoleChangeRequest {
  requestId: string;
  changeType: 'assign' | 'remove';
  targetPrincipalId: string;
  targetDisplayName: string;
  targetUserPrincipalName: string;
  appRoleId: string;
  appRoleValue: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  requestedByUpn: string;
  requestedAtUtc: string;
  decidedByUpn?: string;
  decidedAtUtc?: string;
  decisionNote?: string;
  verifiedByUpn?: string;
  verifiedAtUtc?: string;
}

interface StaffRoleChangeEvent {
  eventId: string;
  requestId: string;
  eventType: 'requested' | 'approved' | 'rejected' | 'verified';
  actorUpn: string;
  createdAtUtc: string;
}

interface StaffRoleAdminPayload {
  sourceOfTruth: 'microsoft_entra';
  writeMode: 'approved_manual_entra_change';
  directory: {
    applicationDisplayName: string;
    applicationId: string;
    servicePrincipalId: string;
    roles: StaffAppRole[];
    assignments: StaffRoleAssignment[];
  };
  requests: StaffRoleChangeRequest[];
  events: StaffRoleChangeEvent[];
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const payload = await response.json().catch(() => ({})) as { message?: string; error?: string };
  return payload.message || payload.error || fallback;
};

const formatDate = (value?: string): string => value ? new Date(value).toLocaleString() : '—';

const StaffRoleAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const hasAccess = tenant?.capabilities?.canManageStaffRoles === true;
  const [data, setData] = React.useState<StaffRoleAdminPayload | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [changeType, setChangeType] = React.useState<'assign' | 'remove'>('assign');
  const [userPrincipalName, setUserPrincipalName] = React.useState('');
  const [appRoleId, setAppRoleId] = React.useState('');
  const [reason, setReason] = React.useState('');

  const load = React.useCallback(async (): Promise<void> => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/staff-roles');
      if (!response.ok) {
        throw new Error(await readError(response, `Unable to load staff roles (${response.status}).`));
      }
      const payload = await response.json() as StaffRoleAdminPayload;
      setData(payload);
      setAppRoleId((current) => current || payload.directory.roles[0]?.id || '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const submitRequest = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetch('/api/admin/staff-role-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changeType, userPrincipalName, appRoleId, reason })
      });
      if (!response.ok) {
        throw new Error(await readError(response, `Unable to create the request (${response.status}).`));
      }
      setMessage('The role change is pending approval by a different Staff Roles administrator.');
      setUserPrincipalName('');
      setReason('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (request: StaffRoleChangeRequest, decision: 'approved' | 'rejected'): Promise<void> => {
    const promptText = decision === 'approved'
      ? 'Optional approval note:'
      : 'Why is this request being rejected?';
    const note = window.prompt(promptText, '') ?? '';
    if (decision === 'rejected' && note.trim().length < 5) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetch(`/api/admin/staff-role-requests/${request.requestId}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note })
      });
      if (!response.ok) {
        throw new Error(await readError(response, `Unable to record the decision (${response.status}).`));
      }
      setMessage(decision === 'approved'
        ? 'Approved. An authorised operator must now make the matching change in Microsoft Entra.'
        : 'The request was rejected and retained in the audit history.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (request: StaffRoleChangeRequest): Promise<void> => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetch(`/api/admin/staff-role-requests/${request.requestId}/verify`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await readError(response, `Unable to verify the role change (${response.status}).`));
      }
      setMessage('Microsoft Entra now shows the approved change. The request is complete.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const prepareRemoval = (assignment: StaffRoleAssignment): void => {
    if (assignment.principalType.toLowerCase() !== 'user') {
      setError('Group-based assignments must be handled directly in Microsoft Entra and cannot use the person request form.');
      return;
    }
    setChangeType('remove');
    setUserPrincipalName(assignment.principalDisplayName.includes('@') ? assignment.principalDisplayName : '');
    setAppRoleId(assignment.appRoleId);
    setReason('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!hasAccess) {
    return (
      <div className="card">
        <h1 className="section-title">Staff Roles</h1>
        <p className="muted">The Manage Edinshall Employees role is required.</p>
      </div>
    );
  }

  const currentUser = (tenant?.userEmail ?? '').toLowerCase();

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      <div>
        <h1 className="section-title">Staff Roles</h1>
        <p className="muted">
          Microsoft Entra is the source of truth. This page reads the live Portal enterprise-application assignments,
          records a reason and separate approval, and verifies the completed Entra change. It never grants a role from its own database.
        </p>
      </div>

      {error && <div className="pill warning">{error}</div>}
      {message && <div className="pill success">{message}</div>}

      <div className="card" style={{ display: 'grid', gap: '12px' }}>
        <h3 style={{ margin: 0 }}>Request a role change</h3>
        <p className="muted" style={{ margin: 0 }}>
          Enter the staff member's Edinshall Microsoft 365 sign-in. A different Staff Roles administrator must approve the request.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 0.5fr) minmax(240px, 1.5fr) minmax(220px, 1fr)', gap: '12px' }}>
          <label>
            <div className="muted">Change</div>
            <select className="input" value={changeType} onChange={(event) => setChangeType(event.target.value as 'assign' | 'remove')}>
              <option value="assign">Assign role</option>
              <option value="remove">Remove role</option>
            </select>
          </label>
          <label>
            <div className="muted">Staff sign-in</div>
            <input className="input" type="email" value={userPrincipalName} onChange={(event) => setUserPrincipalName(event.target.value)} placeholder="person@edinshall.net" />
          </label>
          <label>
            <div className="muted">Portal role</div>
            <select className="input" value={appRoleId} onChange={(event) => setAppRoleId(event.target.value)}>
              {(data?.directory.roles ?? []).map((role) => <option key={role.id} value={role.id}>{role.displayName}</option>)}
            </select>
          </label>
        </div>
        <label>
          <div className="muted">Business reason</div>
          <textarea className="input" rows={3} maxLength={2000} value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        <div>
          <button className="button" disabled={busy || !userPrincipalName || !appRoleId || reason.trim().length < 10} onClick={() => void submitRequest()}>
            Submit for approval
          </button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>Live Microsoft Entra assignments</h3>
            <p className="muted" style={{ marginBottom: 0 }}>
              {data?.directory.applicationDisplayName ?? 'Portal enterprise application'} · refreshed when this page loads
            </p>
          </div>
          <button className="button secondary" disabled={loading || busy} onClick={() => void load()}>Refresh from Entra</button>
        </div>
        {loading && <p className="muted">Loading live assignments…</p>}
        {!loading && (data?.directory.assignments.length ?? 0) === 0 && <p className="muted">No direct user or group role assignments were returned.</p>}
        {(data?.directory.assignments.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto', marginTop: '12px' }}>
            <table className="table">
              <thead><tr><th>Person or group</th><th>Type</th><th>Role</th><th>Assigned</th><th>Action</th></tr></thead>
              <tbody>
                {data?.directory.assignments.map((assignment) => (
                  <tr key={assignment.assignmentId}>
                    <td>{assignment.principalDisplayName}</td>
                    <td>{assignment.principalType}</td>
                    <td>{assignment.appRoleDisplayName}</td>
                    <td>{formatDate(assignment.createdDateTime)}</td>
                    <td>{assignment.principalType.toLowerCase() === 'user'
                      ? <button className="button secondary" disabled={busy} onClick={() => prepareRemoval(assignment)}>Request removal</button>
                      : <span className="muted">Manage the group assignment in Entra</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Change requests and approvals</h3>
        <p className="muted">
          After approval, make the exact change in <a href="https://entra.microsoft.com/" target="_blank" rel="noreferrer">Microsoft Entra admin center</a>
          {' '}under Enterprise applications → {data?.directory.applicationDisplayName ?? 'the Portal application'} → Users and groups. Then return here and verify it.
        </p>
        {(data?.requests.length ?? 0) === 0 && <p className="muted">No staff role change requests have been recorded.</p>}
        {(data?.requests.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Requested</th><th>Change</th><th>Staff member</th><th>Role</th><th>Reason and audit</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {data?.requests.map((request) => {
                  const ownRequest = request.requestedByUpn.toLowerCase() === currentUser;
                  const ownRole = request.targetUserPrincipalName.toLowerCase() === currentUser;
                  const cannotDecide = ownRequest || ownRole;
                  return (
                    <tr key={request.requestId}>
                      <td>{formatDate(request.requestedAtUtc)}<br /><span className="muted">{request.requestedByUpn}</span></td>
                      <td>{request.changeType === 'assign' ? 'Assign' : 'Remove'}</td>
                      <td>{request.targetDisplayName}<br /><span className="muted">{request.targetUserPrincipalName}</span></td>
                      <td>{request.appRoleValue}</td>
                      <td>{request.reason}{request.decidedByUpn && <><br /><span className="muted">Decided by {request.decidedByUpn} · {formatDate(request.decidedAtUtc)}{request.decisionNote ? ` · ${request.decisionNote}` : ''}</span></>}</td>
                      <td><span className={`pill ${request.status === 'rejected' ? 'warning' : request.status === 'completed' ? 'success' : ''}`}>{request.status}</span></td>
                      <td>
                        {request.status === 'pending' && (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button className="button" disabled={busy || cannotDecide} title={cannotDecide ? 'A different, unaffected administrator must decide this request.' : undefined} onClick={() => void decide(request, 'approved')}>Approve</button>
                            <button className="button secondary" disabled={busy || cannotDecide} onClick={() => void decide(request, 'rejected')}>Reject</button>
                          </div>
                        )}
                        {request.status === 'approved' && <button className="button" disabled={busy || ownRequest || ownRole} title={ownRequest || ownRole ? 'A different, unaffected administrator must verify this change.' : undefined} onClick={() => void verify(request)}>Verify Entra change</button>}
                        {request.status === 'completed' && <span className="muted">Verified by {request.verifiedByUpn}<br />{formatDate(request.verifiedAtUtc)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Immutable audit events</h3>
        <p className="muted">The latest request, approval, rejection and Microsoft Entra verification events are retained independently of the current request status.</p>
        {(data?.events ?? []).slice(0, 50).map((event) => (
          <div key={event.eventId} style={{ padding: '8px 0', borderTop: '1px solid var(--colorNeutralStroke2)' }}>
            <strong>{event.eventType}</strong> · {event.actorUpn} · {formatDate(event.createdAtUtc)}
            <div className="muted">Request {event.requestId}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffRoleAdmin;
